// Models
import {
  ActionTriggerModel,
  AlertActionTriggerModel,
  AlertComponentActionTriggerModel,
  CaseActionTriggerModel,
} from 'app/modules/webhooks/models';
import { CustomTableConfig } from 'app/shared/CustomConfig/models';
import { RefreshJWTResponse } from 'app/modules/session/responses';
import { OrgFeature } from 'app/modules/orgManagement/models';
import { FullTeamResponse } from 'app/modules/teams/responses';
import { TeamPermissions } from 'app/modules/teams/models';
import { AgentCurrentStatus } from 'app/modules/agents/models';

export enum SessionActionTypes {
  START_SESSION = '@@session/START_SESSION',
  START_SESSION_SUCCESS = '@@session/START_SESSION_SUCCESS',
  START_SESSION_ERROR = '@@session/START_SESSION_ERROR',
  LOGIN = '@@session/LOGIN',

  SEND_AUTH_EMAIL = '@@session/SEND_AUTH_EMAIL',
  EMAIL_TOKEN_EXCHANGE = '@@session/EMAIL_TOKEN_EXCHANGE',

  TOKEN_PING = '@@session/TOKEN_PING',
  TOKEN_PING_SUCCESS = '@@session/TOKEN_PING_SUCCESS',

  RETRIEVE_IDP = '@@session/RETRIEVE_IDP',
  RETRIEVE_IDP_SUCCESS = '@@session/RETRIEVE_IDP_SUCCESS',

  RETRIEVE_ACTION_TRIGGERS = '@@session/RETRIEVE_ACTION_TRIGGERS',
  RETRIEVE_ACTION_TRIGGERS_SUCCESS = '@@session/RETRIEVE_ACTION_TRIGGERS_SUCCESS',

  SET_SESSION_AGENT_PERMISSIONS = '@@session/SET_SESSION_AGENT_PERMISSIONS',

  SET_LAST_REQUEST_DATE = '@@session/SET_LAST_REQUEST_DATE',

  INIT_INTERCOM = '@@session/INIT_INTERCOM',

  UPDATE_AUTH_EMAIL_SENT = '@@session/UPDATE_AUTH_EMAIL_SENT',

  UPDATE_SESSION_AGENT_NAME_SUCCESS = '@@session/UPDATE_SESSION_AGENT_NAME_SUCCESS',
  UPDATE_SESSION_AGENT_PICTURE_SUCCESS = '@@session/UPDATE_SESSION_AGENT_PICTURE_SUCCESS',
  UPDATE_SESSION_AGENT_AVAILABILITY_SUCCESS = '@@session/UPDATE_SESSION_AGENT_AVAILABILITY_SUCCESS',
  UPDATE_SESSION_AGENT_DISPLAY_TIMEZONE_SUCCESS = '@@session/UPDATE_SESSION_AGENT_DISPLAY_TIMEZONE_SUCCESS',

  START_IMPERSONATION = '@@session/START_IMPERSONATION',
  CANCEL_IMPERSONATION = '@@session/CANCEL_IMPERSONATION',
}

export interface Auth0Config {
  readonly domain: string | undefined;
  readonly clientId: string | undefined;
  readonly loginRedirectUrl: string | undefined;
  readonly logoutRedirectUrl: string | undefined;
  readonly apiAudience: string | undefined;
  readonly apiBaseUrl?: string;
}

export interface SessionAgent {
  accessible_queues: number[];
  auth_provider?: string;
  can_impersonate?: boolean;
  created_at: string;
  current_status: AgentCurrentStatus | null;
  custom_configs?: CustomTableConfig[];
  default_org_display_name: string;
  default_org_id: number;
  display_timezone: string | null;
  email: string;
  full_name: string;
  has_valid_auth?: boolean;
  id: number;
  is_default_login: boolean;
  is_parent_org?: boolean;
  is_reviewer: null | boolean;
  org_display_name: string;
  org_features: OrgFeature[];
  org_id: number;
  org_name: string;
  parent_org_name?: string;
  picture: string;
  recently_merged?: boolean;
  status: string;
  teams: FullTeamResponse[];
  teams_permissions: Partial<TeamPermissions>;
  unit_id: number;
  unit_name: string;
}

export interface SessionAgentIDP {
  access_token: string;
  expires_in: number;
  // only in initial IDP call
  provider?: string;
  user_id?: string;
  isSocial?: true;
  connection?: string;
  // only in refresh IDP call
  id_token?: string;

  scope?: string;
  token_type?: string;
}

export interface ActionTriggerState {
  alertTriggers: AlertActionTriggerModel[];
  alertComponentTriggers: AlertComponentActionTriggerModel[];
  entityTriggers: ActionTriggerModel[];
  caseTriggers: CaseActionTriggerModel[];
  ruleTriggers: ActionTriggerModel[];
  eventTriggers: ActionTriggerModel[];
}

export interface SessionState {
  agent: SessionAgent;
  relevantOrgs: SessionRelevantOrgs;
  idp: SessionAgentIDP;
  accessToken: string;
  permissions: string[];
  impersonating: boolean;
  tokenHasExpired: boolean;
  actionTriggers: ActionTriggerState;
  failedTokenPingCount: number;

  // optional, this is used logout out a user if their last request was after a specified time of inactivity in lumos_config.logout_after_inactivity, which is another optional param
  // we don't want to unnecessarily make this update on every request otherwise
  lastRequestDate?: Date;
}

export interface SessionRelevantOrgs {
  org_id: number;
  org_type: string;
  org_name: string;
  org_display_name?: string;
  parent_org_id?: number;
  parent_org_name?: string;
  parent_org_display_name?: string;
}

export interface AuthClient {
  isAuthenticated: () => boolean;
  renewSession: () => void;
  login: () => Promise<void>;
  scheduleCheckLastActivity: () => void;
  provider: 'auth0' | 'unit21' | '';
  logout: (showLogoutModal?: boolean) => Promise<void>;
  loadingFlags: boolean;
  hasRefreshedToken: boolean;
  authorize: () => void;
  loginSuccess: (authResponse: RefreshJWTResponse) => void;
  setConnectionType: (connectionType: string | undefined) => void;
}

export interface EmailTokenExchangeType {
  token: string;
  auth?: AuthClient;
}

export interface Auth0AuthArgs {
  scope: string;
  accessType: string;
  approvalPrompt?: 'force';
}
